import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IStation } from '../../ts/interface/station.interface';
import { DEFAULT_ROWS_PER_PAGE, LOCKER } from '../../constants';
import { changeTab, findAccess, history } from '../../utils';
import { connect } from 'react-redux';
import { stationConfigurations } from '../../util/stations';
import { makeColumnsV2 } from '../../tableColumns/stationColumns';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { StationProps } from '../../ts/interface/pages/stations.interfaces';
import PageContainer from '../../components/Redesing/page-container';
import AddIcon from '@mui/icons-material/Add';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../ts/interface/components/redesing/table.interface';
import Card from '@mui/material/Card';
import { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { SearchChip } from '../../ts/interface/components/table.interface';
import { MovaticFieldGroupSingleSelectFilter } from '../../components/Redesing/movatic-field-group';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useParams } from 'react-router-dom';
import PageContent from '../../components/Redesing/page-content';
import StationLocationsMap from './stationLocationsMap';
import useUrlState from '../../hooks/use-url-state';
import { LocationStatus } from '../../ts/enums';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';
import {
  useLocationsRelayQuery,
  mapLocationsToIStations,
  LocationQueryFilter,
} from '../../hooks/stations/query/V2';
import { STATION_STATUS, statusToStateNumber, sortKeyToField } from './shared';

const Station = ({
  system,
  movaticAccess,
  modeInfo,
  systemAccess,
  systemLoaded,
}: StationProps) => {
  const [selectFilter, setSelectFilter] = useState<string>(STATION_STATUS[0].value);
  const [exportData, setExportData] = useState<boolean>(false);
  const { currentTab = 'locations' } = useParams<{ currentTab: string }>();
  const [currentTabV, setCurrentTabV] = useState(currentTab);
  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();
  const [pageState, setPageState, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    status: STATION_STATUS[0].value as LocationStatus | string,
    search: null as string | null,
    sortConfig: {
      key: 'name',
      direction: 'asc',
    } as ISortConfig,
  });
  const debouncedSearchTerm = useSearchDebounce(pageState.search);

  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: React.SetStateAction<string>) => {
      changeTab(value, currentTab, 'locations');
    },
    [currentTab]
  );

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Rebuild chips from the *applied* status filter (the URL-persisted field
  // that drives the query), so chips always match the fetched data — including
  // after a refresh and when the filter is cleared (no chip).
  useEffect(() => {
    if (isRestoring) return;
    // Treat "All" as no status filter (consistent with the Hardware page): no
    // chip when nothing meaningful is selected.
    if (!pageState.status || pageState.status === LocationStatus.all) {
      setChips([]);
      return;
    }
    const option = STATION_STATUS.find((item) => item.value === pageState.status);
    setChips([
      {
        label: 'Status',
        field: 'status',
        value: pageState.status,
        displayValue: option?.label ?? pageState.status,
      },
    ]);
    setSelectFilter(pageState.status);
  }, [isRestoring, pageState.status]);

  const isEnabled = isComplete && systemLoaded;

  // Build GraphQL filter
  const graphqlFilter = useMemo((): LocationQueryFilter => {
    const filter: LocationQueryFilter = {
      systemId: system.gid,
    };

    // Add state filter if not 'all'
    const stateNumber = statusToStateNumber(pageState.status);
    if (stateNumber !== undefined) {
      filter.state = stateNumber;
    }

    // Add name search filter
    if (debouncedSearchTerm) {
      filter.name = debouncedSearchTerm;
    }

    return filter;
  }, [system.gid, pageState.status, debouncedSearchTerm]);

  // Build GraphQL sort
  const graphqlSort = useMemo(() => {
    return [
      {
        field: sortKeyToField(pageState.sortConfig.key) as any,
        order: pageState.sortConfig.direction.toUpperCase() as 'ASC' | 'DESC',
      },
    ];
  }, [pageState.sortConfig.key, pageState.sortConfig.direction]);

  const {
    data: locationsData,
    count: totalLocationCount,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading: isLoadingAllStations,
    isRefetching,
  } = useLocationsRelayQuery(graphqlFilter, {
    limit: pageState.sizePerPage,
    after,
    sort: graphqlSort,
    enabled: isEnabled,
  });

  // Only the current page's rows are shown (server-side pagination).
  const stationsItems = useMemo(() => {
    if (!locationsData) return [];
    return mapLocationsToIStations(locationsData);
  }, [locationsData]);

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsData]);

  const formatStations = (stations: IStation[]) => {
    return stations.map((station) => {
      const available = station.available_hardware || 0;
      const capacity =
        modeInfo.details.hardware_type !== LOCKER ||
        station.configuration === stationConfigurations.ZONE
          ? station.capacity
          : station.totalRentableHardware || 0;
      return { ...station, available, capacity };
    });
  };

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      resetPagination();
      setSelectFilter(STATION_STATUS[0].value);
      setPageState((prev) => ({
        ...prev,
        status: '',
      }));
    },
    [setPageState, resetPagination]
  );

  const handleTypeChange = useCallback(
    (selectedOption: { value: string; label: string }): void => {
      resetPagination();
      setPageState((prev) => ({
        ...prev,
        status: selectedOption.value as LocationStatus,
      }));
    },
    [setPageState, resetPagination]
  );

  return (
    <>
      <PageContainer isLoading={!systemLoaded} spaceName={'Locations'}>
        <PageContent
          dataId="locations"
          detailedView={false}
          tabsList={[
            { label: 'Locations', value: 'locations' },
            {
              label: 'Map',
              value: 'map',
            },
          ]}
          tabsChildren={
            <>
              {currentTabV === 'locations' && (
                <Card>
                  <PaginationTable
                    dataId="locations-list"
                    isLoading={!systemLoaded || isLoadingAllStations || isPaginating}
                    isLoadingCache={isRefetching}
                    buttons={[
                      {
                        label: 'Export',
                        onClick: () => setExportData(true),
                        startIcon: <FileDownloadIcon />,
                      },
                      {
                        startIcon: <AddIcon />,
                        label: 'Add',
                        onClick: () => history.push('/locations/add'),
                        disabled:
                          !findAccess(systemAccess).includes('physical') ||
                          !!movaticAccess,
                      },
                    ]}
                    exportData={exportData}
                    localSearch={false}
                    csvFileName={'Stations.csv'}
                    onExport={() => setExportData(false)}
                    chips={chips}
                    handleChipDelete={handleChipDelete}
                    spaceFilters={
                      <>
                        <MovaticFieldGroupSingleSelectFilter
                          dataId={`location-status-filter`}
                          label={'Status'}
                          value={selectFilter.toString()}
                          onChange={(e: SelectChangeEvent) => {
                            const id = e.target.value as string;
                            setSelectFilter(id);
                          }}
                          options={STATION_STATUS.map((option, index) => (
                            <MenuItem
                              id={`location-status-dropdown-${index}`}
                              key={index}
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        />
                      </>
                    }
                    onApplyFilter={() => {
                      const obj = STATION_STATUS.find(
                        (item) => item.value === selectFilter
                      ) || {
                        value: 'all',
                        label: 'All',
                      };
                      handleTypeChange(obj);
                    }}
                    showFilter
                    noDataSlice
                    onRowCLick={(id: string) => history.push(`/locations/${id}/general`)}
                    sortBy={pageState.sortConfig.key}
                    sortDirection={pageState.sortConfig.direction}
                    onSortChange={(sort) => {
                      resetPagination();
                      setPageState((prev) => ({
                        ...prev,
                        sortConfig: {
                          key: sort.key,
                          direction: sort.direction as 'asc' | 'desc',
                        },
                      }));
                    }}
                    padding={{ pt: 1, pr: 1 }}
                    columns={makeColumnsV2(system) as IColumn[]}
                    items={formatStations(stationsItems || [])}
                    searchPlaceholder={`Search Locations`}
                    page={pageNumber}
                    rowsPerPage={pageState.sizePerPage}
                    hasMore={graphqlHasMore}
                    totalItems={totalLocationCount}
                    onPageChange={(page: any, newPage: number) => onPageChange(newPage)}
                    onRowsPerPageChange={(pageSizeString) => {
                      resetPagination();
                      setPageState((prev) => ({
                        ...prev,
                        sizePerPage: Number(pageSizeString.target.value),
                      }));
                    }}
                    handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
                      resetPagination();
                      setPageState((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }));
                    }}
                    searchText={pageState.search || ''}
                  />
                </Card>
              )}
              {currentTabV === 'map' && <StationLocationsMap />}
            </>
          }
          currentTabV={currentTabV}
          handleTabsChange={handleTabsChange}
        />
      </PageContainer>
    </>
  );
};

const StationR = connect(
  (state: any) => ({
    system: state.system.current,
    auth: state.admin.admin,
    systemAccess: state.admin.systemAccess,
    modeInfo: state.mode,
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(Station);

export default StationR;
